/**
 * WindBarbMap Component
 *
 * This component provides an interactive map of Northern Canada with wind barb markers.
 * It displays wind speed and direction using standard meteorological wind barb symbols.
 *
 * Key Features:
 * - Interactive map with zoom and pan controls
 * - Wind barb markers showing wind speed and direction
 * - User location tracking
 * - Station filtering by type and status
 * - Responsive design for different screen sizes
 * - Dynamic wind barb rotation based on wind direction
 */

import React, { useState, useEffect, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  ZoomControl,
  Popup,
  useMap,
} from "react-leaflet";
import { useNavigate } from "react-router-dom";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import pinIcon from "../Map/gps.png";
import {
  CircularProgress,
  Box,
  Typography,
  Switch,
  Button,
  Chip,
} from "@mui/material";
import { useWeatherStationsWindMap } from "../../services/useWeatherStationsWindMap";
import { useTranslation } from "../../context/TranslationContext";
import {
  createUrlFriendlyName,
  findStationByNameOrId,
} from "../../utils/rawFunction";

const DEFAULT_CENTER = [69.49408333333334, -89.69515];
const DEFAULT_ZOOM = 4.5;
const WIND_LEGEND_GRADIENT =
  "linear-gradient(to right, rgb(98, 113, 184), rgb(98, 113, 184), rgb(98, 113, 184), rgb(98, 113, 184), rgb(61, 110, 163), rgb(74, 148, 170), rgb(74, 146, 148), rgb(77, 142, 124), rgb(76, 164, 76), rgb(103, 164, 54), rgb(162, 135, 64), rgb(162, 109, 92), rgb(141, 63, 92), rgb(151, 75, 145), rgb(95, 100, 160), rgb(91, 136, 161), rgb(91, 136, 161))";

const WIND_SPEED_COLOR_STEPS = [
  {
    max: 5,
    name: "0-4 kt",
    color: "rgb(98, 113, 184)",
    gradient: "rgba(98, 113, 184, 0.35)",
  },
  {
    max: 10,
    name: "5-9 kt",
    color: "rgb(61, 110, 163)",
    gradient: "rgba(61, 110, 163, 0.35)",
  },
  {
    max: 20,
    name: "10-19 kt",
    color: "rgb(74, 146, 148)",
    gradient: "rgba(74, 146, 148, 0.35)",
  },
  {
    max: 30,
    name: "20-29 kt",
    color: "rgb(76, 164, 76)",
    gradient: "rgba(76, 164, 76, 0.35)",
  },
  {
    max: 40,
    name: "30-39 kt",
    color: "rgb(162, 135, 64)",
    gradient: "rgba(162, 135, 64, 0.35)",
  },
  {
    max: 60,
    name: "40-59 kt",
    color: "rgb(141, 63, 92)",
    gradient: "rgba(141, 63, 92, 0.35)",
  },
  {
    max: Number.POSITIVE_INFINITY,
    name: "60+ kt",
    color: "rgb(91, 136, 161)",
    gradient: "rgba(91, 136, 161, 0.35)",
  },
];
const WIND_LEGEND_STEPS_KT = [0, 5, 10, 20, 30, 40, 60];

/**
 * Wind speed categories based on legend ramp thresholds.
 * Returns text, color, and glow color for given speed in knots.
 */
const getWindCategory = (speedKnots) => {
  const speed = Number.isFinite(speedKnots) ? speedKnots : 0;
  return (
    WIND_SPEED_COLOR_STEPS.find((step) => speed < step.max) ||
    WIND_SPEED_COLOR_STEPS[WIND_SPEED_COLOR_STEPS.length - 1]
  );
};

/**
 * Creates a custom pin icon with text label for user location
 */
const createCustomPinIcon = (iconUrl, text = "", color = "black") => {
  const html = `
    <div style='text-align: center; position: relative;'>
      <img src='${iconUrl}' style='width: 40px; height: 40px;' />
      <div style='font-size: 12px; color: ${color}; border: 2px dotted red; display: inline-block; padding: 2px 4px; white-space: nowrap;'>${text}</div>
    </div>
  `;

  return L.divIcon({
    html: html,
    className: "custom-icon",
    iconSize: [40, 60],
    iconAnchor: [20, 60],
  });
};

/**
 * UserLocationMarker Component
 * Displays the user's current location on the map
 */
const UserLocationMarker = ({ isLocationEnabled }) => {
  const [position, setPosition] = useState(null);
  const map = useMap();

  useEffect(() => {
    if (isLocationEnabled) {
      map.locate().on("locationfound", function (e) {
        setPosition(e.latlng);
        map.flyTo(e.latlng, 10);
      });
    } else {
      setPosition(null);
      map.flyTo(DEFAULT_CENTER, DEFAULT_ZOOM);
    }
  }, [map, isLocationEnabled]);

  return position === null ? null : (
    <Marker
      position={position}
      icon={createCustomPinIcon(pinIcon, "You are here", "red")}
    >
      <Popup>You are here</Popup>
    </Marker>
  );
};

/**
 * Main WindBarbMap Component
 */
const WindBarbMap = () => {
  const MapController = () => {
    const map = useMap();

    useEffect(() => {
      if (map) {
        setTimeout(() => {
          map.invalidateSize();
        }, 100);
      }
    }, [map]);

    return null;
  };

  const { translate } = useTranslation();
  const navigate = useNavigate();

  // Wind station data fetching
  const {
    data: windStations,
    isLoading,
    isFetching,
    error,
  } = useWeatherStationsWindMap({ enabled: true });

  // State management
  const [isLocationEnabled, setIsLocationEnabled] = useState(false);
  const mapRef = useRef(null);
  const [currentZoom, setCurrentZoom] = useState(DEFAULT_ZOOM);
  const [stationFilter, setStationFilter] = useState("all");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [speedUnit, setSpeedUnit] = useState("kt");

  const toSelectedUnit = (speedKnots) =>
    speedUnit === "km/h" ? speedKnots * 1.852 : speedKnots;

  const formatLegendTick = (speedKnots) =>
    `${Math.round(toSelectedUnit(speedKnots))}`;

  const toggleSpeedUnit = () => {
    setSpeedUnit((prev) => (prev === "kt" ? "km/h" : "kt"));
  };

  // Handle screen size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Track and update map zoom level
  const ZoomLevelUpdater = () => {
    const map = useMap();

    useEffect(() => {
      const updateZoom = () => {
        setCurrentZoom(map.getZoom());
      };

      map.on("zoomend", updateZoom);
      updateZoom();
      return () => {
        map.off("zoomend", updateZoom);
      };
    }, [map]);

    return null;
  };

  // Handle station click navigation
  const handleStationClick = (stationId) => {
    const station = findStationByNameOrId(stationId);
    if (station) {
      const urlFriendlyName = createUrlFriendlyName(station.name);
      navigate(`/${urlFriendlyName}`);
    }
  };

  // Toggle location tracking
  const handleLocationToggle = () => {
    setIsLocationEnabled(!isLocationEnabled);
  };

  // Handle station filter changes
  const handleFilterChange = (filterValue) => {
    setStationFilter(filterValue);
  };

  // Filter stations based on selected criteria
  const getFilteredStations = () => {
    if (!windStations) return [];
    if (stationFilter === "all") return windStations;

    return windStations.filter((station) => {
      if (stationFilter === "Community-led")
        return station.category === "Community-led";
      if (stationFilter === "Research")
        return station.category === "Research" || station.category === "";
      if (stationFilter === "Government")
        return station.category === "Government";
      if (stationFilter === "operational")
        return station.status === "Operational";

      return true;
    });
  };

  const filteredStations = getFilteredStations();

  // Handle map resize
  useEffect(() => {
    const handleResize = () => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Error handling
  if (error)
    return (
      <Typography color="error">An error occurred: {error.message}</Typography>
    );

  // Loading state
  const showLoadingIndicator = isLoading || isFetching;

  /**
   * Creates a custom wind barb icon with rotation based on wind direction
   * @param {string} windBarbIcon - Wind barb icon filename (wind speed)
   * @param {number} windDirection - Wind direction in degrees
   * @param {number} windSpeedKnots - Wind speed in knots
   * @param {string} stationName - Name of the station
   * @param {boolean} hasError - Whether the station has an error
   * @param {string} status - Station status
   * @returns {L.DivIcon} - Custom Leaflet icon
   */
  const createWindBarbIcon = (
    windBarbIcon,
    windDirection,
    windSpeedKnots,
    stationName,
    info,
    hasError = false,
    status,
    hasValidData = true
  ) => {
    // Use exponential scaling for smoother zoom transitions
    // Icons grow larger as you zoom in, smaller as you zoom out
    const zoomFactor = Math.pow(1.5, currentZoom - DEFAULT_ZOOM); // Increased from 1.15 to 1.5 for more aggressive scaling
    const baseSize = 80 * zoomFactor;
    // Tie label sizes to baseSize so station names / status track zoom like the barb (not capped while icon keeps growing)
    const nameFontSize = Math.max(
      8,
      Math.min(22, Math.round(baseSize * 0.125))
    );
    const statusFontSize = Math.max(
      7,
      Math.min(18, Math.round(baseSize * 0.1))
    );
    // Smaller type for text-only markers (Not Available / Non-Operational) so labels stay readable but not dominant
    const textOnlyNameFontSize = Math.max(
      6,
      Math.min(14, Math.round(baseSize * 0.085))
    );
    const textOnlyStatusFontSize = Math.max(
      5,
      Math.min(11, Math.round(baseSize * 0.068))
    );
    const labelOffsetAboveBarb = Math.round(baseSize * 0.4375); // was fixed -35px for 80px barb
    const showDetails = currentZoom >= 4;

    // Get wind category for color
    const windCategory = getWindCategory(windSpeedKnots || 0);
    const windColor = windCategory.color;
    const gradientColor = windCategory.gradient;

    let statusText = "";
    let textColor = "blue";
    let opacity = 1;

    // Determine status display
    if (status === "Non-Operational") {
      statusText = "Non-Operational";
      textColor = "#ad4242";
      opacity = 0.6;
    } else if (status === "Warning") {
      statusText = "Warning";
      textColor = "#d4a012";
      opacity = 0.7;
    } else if (!hasValidData || hasError) {
      statusText = "Not Available";
      textColor = "#666666";
      opacity = 0.7;
    } else {
      statusText = info || "Operational";
      textColor = "blue";
    }

    // Wind barb SVG path
    const windBarbPath = `/Qulle-wind-barbs/${windBarbIcon}.svg`;
    // const windBarbPath = `/wind-barbs/${windBarbIcon}.svg`;

    // For non-operational or invalid data, show only station name with status
    // No wind barb icon, just the text label
    if (status === "Non-Operational" || !hasValidData || hasError) {
      const displayStatus =
        status === "Non-Operational" ? "(Non-Operational)" : "(Not Available)";

      const html = `
        <div style='
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          text-align: center;
          white-space: nowrap;
        '>
          <div style='
            font-size: ${textOnlyNameFontSize}px; 
            color: #333;
            font-weight: 500;
            text-shadow: 
              -1px -1px 0 #fff,
              1px -1px 0 #fff,
              -1px 1px 0 #fff,
              1px 1px 0 #fff,
              0 0 3px rgba(255,255,255,0.8);
            line-height: 1.2;
          '>
            ${translate(stationName)}
          </div>
          <div style='
            font-size: ${textOnlyStatusFontSize}px; 
            color: ${status === "Non-Operational" ? "#ad4242" : "#666"};
            font-weight: 500;
            text-shadow: 
              -1px -1px 0 #fff,
              1px -1px 0 #fff,
              -1px 1px 0 #fff,
              1px 1px 0 #fff,
              0 0 3px rgba(255,255,255,0.8);
            margin-top: ${Math.max(2, Math.round(textOnlyNameFontSize * 0.15))}px;
          '>
            ${displayStatus}
          </div>
        </div>
      `;

      return L.divIcon({
        html: html,
        className: "custom-wind-barb-icon text-only",
        iconSize: [1, 1],
        iconAnchor: [0, 0],
        popupAnchor: [0, -20],
      });
    }

    // Valid data - show wind barb with gradient glow effect.
    // The barb container is the element centered on the station coordinate
    // (translate(-50%, -50%)), so the barb's visual center lands exactly on the
    // point. The station-name label is absolutely positioned relative to the
    // barb so it does NOT add to the centered box (which would push the barb off
    // the point by a zoom-dependent amount). Mirrors the kiosk overlay approach.
    const html = `
      <div style='
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
      '>
        <div style='position: relative; display: inline-block;'>
          <!-- Gradient glow effect background -->
          <div style='
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: ${baseSize * 1.5}px;
            height: ${baseSize * 1.5}px;
            background: radial-gradient(circle, ${windColor} 0%, ${gradientColor} 30%, transparent 70%);
            border-radius: 50%;
            z-index: 0;
          '></div>

          <!-- Wind barb icon rotated -->
          <div style='
            position: relative;
            transform: rotate(${windDirection || 0}deg);
            z-index: 1;
          '>
            <img
              src='${windBarbPath}'
              style='
                width: ${baseSize}px;
                height: ${baseSize}px;
                opacity: ${opacity};
                filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
                display: block;
              '
              onerror="this.style.display='none'"
            />
          </div>
          ${
            showDetails
              ? `
          <!-- Station name label: absolutely positioned so it doesn't shift the barb's center -->
          <div style='
            position: absolute;
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            margin-top: -${labelOffsetAboveBarb}px;
            font-size: ${nameFontSize}px;
            color: #333;
            font-weight: 600;
            text-shadow:
              -1px -1px 0 #fff,
              1px -1px 0 #fff,
              -1px 1px 0 #fff,
              1px 1px 0 #fff,
              0 0 3px rgba(255,255,255,0.9);
            white-space: nowrap;
            z-index: 2;
          '>
            ${translate(stationName)}
          </div>
        `
              : ""
          }
        </div>
      </div>
    `;

    return L.divIcon({
      html: html,
      className: "custom-wind-barb-icon",
      iconSize: [1, 1],
      iconAnchor: [0, 0],
      popupAnchor: [0, -baseSize / 2],
    });
  };

  return (
    <Box
      sx={{
        position: "relative",
        height: "100vh",
        width: "100%",
        border: "1px solid black",
        "& .leaflet-container": {
          width: "100%",
          height: "100%",
        },
      }}
    >
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        style={{ height: "100%", width: "100%" }}
        zoomControl={false}
        minZoom={3}
        maxZoom={10}
        scrollWheelZoom={true}
        ref={mapRef}
        whenReady={(map) => {
          map.target.invalidateSize();
        }}
      >
        <MapController />
        <ZoomLevelUpdater />

        <TileLayer
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        {filteredStations &&
          filteredStations.map((station) => {
            // Check if wind data has valid QC
            const hasValidWindSpeed =
              station.windSpeedQc === undefined || station.windSpeedQc === 100;
            const hasValidWindDirection =
              station.windDirectionQc === undefined ||
              station.windDirectionQc === 100;
            const hasValidData =
              hasValidWindSpeed &&
              hasValidWindDirection &&
              station.windSpeed !== null &&
              station.windDirection !== null;

            return (
              <Marker
                key={station.id}
                position={[station.lat, station.lon]}
                icon={createWindBarbIcon(
                  station.windBarbIcon,
                  station.windDirection,
                  station.windSpeedKnots,
                  station.name,
                  station.info,
                  station.hasError,
                  station.status,
                  hasValidData
                )}
                eventHandlers={{ click: () => handleStationClick(station.id) }}
              >
                <Popup>
                  <div>
                    <p>
                      <strong>Station:</strong> {station.name}
                    </p>
                    <p>
                      <strong>Wind Speed:</strong>{" "}
                      {hasValidData && Number.isFinite(station.windSpeedKnots)
                        ? `${Math.round(
                            toSelectedUnit(station.windSpeedKnots)
                          )} ${speedUnit} (${
                            getWindCategory(station.windSpeedKnots).name
                          })`
                        : "Not Available"}
                    </p>
                    <p>
                      <strong>Wind Direction:</strong>{" "}
                      {hasValidData && Number.isFinite(station.windDirection)
                        ? `${Math.round(station.windDirection)}°`
                        : "Not Available"}
                    </p>
                    <p>
                      <strong>Status:</strong> {station.status}
                    </p>
                    {!hasValidData && station.status === "Operational" && (
                      <p style={{ color: "#666", fontStyle: "italic" }}>
                        <strong>QC Status:</strong> Data quality check failed
                      </p>
                    )}
                  </div>
                </Popup>
              </Marker>
            );
          })}
        <UserLocationMarker isLocationEnabled={isLocationEnabled} />
        <ZoomControl position="bottomright" />
      </MapContainer>

      {/* Filter buttons at the top */}
      <Box
        sx={{
          position: "absolute",
          top: "10px",
          left: "10px",
          right: "10px",
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          alignItems: "center",
        }}
      >
        {/* Filter buttons row */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "6px",
            justifyContent: "center",
          }}
        >
          <Button
            variant={stationFilter === "all" ? "contained" : "outlined"}
            color="primary"
            size="small"
            onClick={() => handleFilterChange("all")}
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              backgroundColor:
                stationFilter === "all" ? "primary.main" : "white",
              minWidth: "auto",
              px: 1.5,
              py: 0.5,
              fontSize: "0.75rem",
              boxShadow: stationFilter === "all" ? 2 : 1,
            }}
          >
            {translate("All")}
          </Button>

          <Button
            variant={
              stationFilter === "Community-led" ? "contained" : "outlined"
            }
            color="primary"
            size="small"
            onClick={() => handleFilterChange("Community-led")}
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              backgroundColor:
                stationFilter === "Community-led" ? "primary.main" : "white",
              minWidth: "auto",
              px: 1.5,
              py: 0.5,
              fontSize: "0.75rem",
              boxShadow: stationFilter === "Community-led" ? 2 : 1,
            }}
          >
            {translate("Community-led")}
          </Button>
          <Button
            variant={stationFilter === "Research" ? "contained" : "outlined"}
            color="primary"
            size="small"
            onClick={() => handleFilterChange("Research")}
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              backgroundColor:
                stationFilter === "Research" ? "primary.main" : "white",
              minWidth: "auto",
              px: 1.5,
              py: 0.5,
              fontSize: "0.75rem",
              boxShadow: stationFilter === "Research" ? 2 : 1,
            }}
          >
            {translate("Research")}
          </Button>
          <Button
            variant={stationFilter === "Government" ? "contained" : "outlined"}
            color="primary"
            size="small"
            onClick={() => handleFilterChange("Government")}
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              backgroundColor:
                stationFilter === "Government" ? "primary.main" : "white",
              minWidth: "auto",
              px: 1.5,
              py: 0.5,
              fontSize: "0.75rem",
              boxShadow: stationFilter === "Government" ? 2 : 1,
            }}
          >
            {translate("Government")}
          </Button>

          <Button
            variant={stationFilter === "operational" ? "contained" : "outlined"}
            color="primary"
            size="small"
            onClick={() => handleFilterChange("operational")}
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              backgroundColor:
                stationFilter === "operational" ? "primary.main" : "white",
              minWidth: "auto",
              px: 1.5,
              py: 0.5,
              fontSize: "0.75rem",
              boxShadow: stationFilter === "operational" ? 2 : 1,
            }}
          >
            {translate("Operational")}
          </Button>
        </Box>

        {/* Find My Location button */}
        <Box
          sx={{
            backgroundColor: "white",
            padding: "6px 12px",
            borderRadius: "20px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            userSelect: "none",
            transition: "background-color 0.2s",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.05)",
            },
            width: "auto",
            alignSelf: "center",
          }}
          onClick={handleLocationToggle}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              color: isLocationEnabled ? "primary.main" : "text.primary",
              fontSize: "0.75rem",
              mr: 1,
            }}
          >
            {translate("Find My Location")}
          </Typography>
          <Switch
            checked={isLocationEnabled}
            color="primary"
            size="small"
            sx={{
              "& .MuiSwitch-thumb": {
                backgroundColor: isLocationEnabled
                  ? "primary.main"
                  : "grey.400",
              },
              "& .MuiSwitch-track": {
                backgroundColor: isLocationEnabled
                  ? "primary.light"
                  : "grey.300",
              },
              transform: "scale(0.8)",
            }}
          />
        </Box>

        {/* Station count chip */}
        {stationFilter !== "all" && (
          <Chip
            label={`${filteredStations.length} ${translate("stations")}`}
            size="small"
            color="primary"
            variant="outlined"
            onDelete={() => setStationFilter("all")}
            sx={{
              backgroundColor: "white",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
            }}
          />
        )}
      </Box>

      {/* Wind Speed Legend */}
      <Box
        sx={{
          position: "absolute",
          ...(isMobile
            ? { bottom: "0", left: "0", right: "0" } // Docked strip on mobile
            : { top: "40px", right: "20px" }), // Top-right on desktop
          zIndex: 1000,
          width: isMobile ? "100%" : "340px",
          maxWidth: "100%",
          backgroundColor: isMobile ? "transparent" : "#ffffff",
          borderRadius: "14px",
          border: isMobile ? "none" : "1px solid rgba(15, 23, 42, 0.12)",
          padding: isMobile ? "0" : "10px",
          boxShadow: isMobile ? "none" : "0 4px 12px rgba(15, 23, 42, 0.16)",
          "& .rhbottom__legend": {
            display: "flex",
            alignItems: "center",
            height: isMobile ? "18px" : "26px",
            width: "100%",
            borderRadius: isMobile ? "0" : "13px",
            overflow: "hidden",
            background: WIND_LEGEND_GRADIENT,
            boxShadow: isMobile ? "none" : "0 2px 8px rgba(15, 23, 42, 0.22)",
          },
          "& .rhbottom__legend > span": {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: "#ffffff",
            fontSize: isMobile ? "0.46rem" : "0.68rem",
            fontWeight: 600,
            lineHeight: 1,
            textShadow: "0 1px 2px rgba(0, 0, 0, 0.35)",
            userSelect: "none",
          },
        }}
      >
        {!isMobile && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 0.5,
              mb: 0.7,
            }}
          >
            <Typography
              sx={{
                color: "#0f172a",
                fontSize: "0.68rem",
                fontWeight: 700,
                lineHeight: 1,
                userSelect: "none",
              }}
            >
              {translate("Wind Speed")}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {["kt", "km/h"].map((unit) => (
                <Button
                  key={unit}
                  size="small"
                  onClick={() => setSpeedUnit(unit)}
                  sx={{
                    minWidth: "48px",
                    height: "22px",
                    px: 1,
                    borderRadius: "10px",
                    textTransform: "none",
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    color: speedUnit === unit ? "#ffffff" : "#334155",
                    backgroundColor: speedUnit === unit ? "#000000" : "#ffffff",
                    border:
                      speedUnit === unit
                        ? "1px solid #000000"
                        : "1px solid rgba(15, 23, 42, 0.24)",
                    "&:hover": {
                      backgroundColor:
                        speedUnit === unit
                          ? "#000000"
                          : "rgba(15, 23, 42, 0.06)",
                    },
                  }}
                >
                  {unit}
                </Button>
              ))}
            </Box>
          </Box>
        )}
        <div
          className="rhbottom__legend clickable metric-legend noselect tooltip--left"
          onClick={isMobile ? toggleSpeedUnit : undefined}
          role={isMobile ? "button" : undefined}
          aria-label={isMobile ? "Toggle wind speed units" : undefined}
          title={isMobile ? `Tap to switch unit (${speedUnit})` : undefined}
          style={isMobile ? { cursor: "pointer" } : undefined}
        >
          <span style={{ width: "12.5%" }}>{speedUnit}</span>
          {WIND_LEGEND_STEPS_KT.map((step) => (
            <span key={step} style={{ width: "12.5%" }}>
              {formatLegendTick(step)}
            </span>
          ))}
        </div>
        {!isMobile && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.35,
              color: "#334155",
              fontSize: "0.64rem",
              textAlign: "center",
              textShadow: "none",
              userSelect: "none",
            }}
          >
            {translate("Arrow points to where wind is blowing to")}
          </Typography>
        )}
      </Box>

      {showLoadingIndicator && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1000,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            padding: "20px",
            borderRadius: "10px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={50} />
          <Box sx={{ mt: 2 }}>Loading Wind Data...</Box>
        </Box>
      )}
    </Box>
  );
};

export default WindBarbMap;
